// shadcn-ui v2 New York preset; extended with a mobileVariant prop for the
// M0 mobile UX foundation. At <md the dialog renders as a bottom sheet
// (default), a full-screen modal, or — if explicitly opted in — the
// original centred overlay. At md+ behaviour is unchanged.
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { useIsBelow } from '@/lib/viewport';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export type DialogMobileVariant = 'sheet' | 'fullscreen' | 'centered';

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  /**
   * Layout at <md. Defaults to `'sheet'` — a bottom sheet sliding up with
   * a drag handle. `'fullscreen'` takes the entire viewport with a sticky
   * Close button. `'centered'` keeps the desktop centered-overlay style.
   * Above md this prop has no effect.
   */
  mobileVariant?: DialogMobileVariant;
}

const SHEET_CONTENT_CLASSES = cn(
  // Bottom-anchored, full-width, capped to viewport height.
  //
  // ⚠️ TWO SPELLINGS OF THE SAME CAP (2026-09-26). `dvh` shipped in Chrome 108;
  // on Chrome 101 (Android 9, measured on a real buyer's phone)
  // `CSS.supports('height','1dvh')` is false, a `dvh` in `max-height` is invalid
  // at computed-value time, and the whole declaration is dropped — `max-height`
  // computes to `none`, so the sheet is not capped at ALL and whatever it cannot
  // show is unreachable rather than merely off-screen. The plain `92vh` is a real
  // cap for those builds; the `supports-[height:1dvh]` twin is emitted inside
  // `@supports` AFTER it at equal specificity, so every modern browser keeps
  // exactly the `92dvh` it had. A consumer that supplies its own `max-h-*` (the
  // payment sheets, which use `var(--sheet-max, …)`) overrides BOTH through
  // twMerge — verified, since these are the base classes under them.
  'fixed left-0 right-0 bottom-0 z-50 w-full max-h-[92vh] supports-[height:1dvh]:max-h-[92dvh]',
  // Surface with rounded top corners + drag handle space.
  'bg-cream border-t-[1.5px] border-charcoal rounded-t-[20px] shadow-2xl',
  // Slide-up animation, <200ms, transform-only.
  'duration-200 will-change-transform',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
  // Flex column for header + scrolling body inside.
  'flex flex-col',
  // Honour iOS bottom home-bar safe area.
  'pb-[env(safe-area-inset-bottom)]',
);

const FULLSCREEN_CONTENT_CLASSES = cn(
  'fixed inset-0 z-50 w-full h-[100dvh]',
  'bg-cream',
  'duration-200',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
  'flex flex-col',
  'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
);

const CENTERED_CONTENT_CLASSES = cn(
  'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
);

/**
 * Bottom-sheet drag handle. Purely visual — Radix Dialog already handles
 * ESC + tap-outside + close button. A small horizontal pill at the top of
 * the sheet tells the user it can be dismissed. Drag-to-dismiss gesture
 * (pointer events translating the sheet) is wired in via the wrapping
 * `<DialogContent>` below when `mobileVariant === 'sheet'`.
 */
function SheetDragHandle() {
  return (
    <div className="flex justify-center pt-2 pb-1 shrink-0">
      <div aria-hidden="true" className="h-1 w-10 rounded-full bg-charcoal/30" />
    </div>
  );
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, mobileVariant = 'sheet', ...props }, ref) => {
  const isMobile = useIsBelow('md');
  // Drag-to-dismiss state — only used for the sheet variant on mobile.
  const [dragY, setDragY] = React.useState<number>(0);
  const dragStartRef = React.useRef<number | null>(null);

  const useSheet = isMobile && mobileVariant === 'sheet';
  const useFullscreen = isMobile && mobileVariant === 'fullscreen';

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!useSheet) return;
    dragStartRef.current = e.clientY;
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!useSheet || dragStartRef.current == null) return;
    const dy = e.clientY - dragStartRef.current;
    if (dy > 0) setDragY(dy);
  }
  function onPointerUp() {
    if (!useSheet) return;
    if (dragY > 80) {
      // Trigger Radix close via a synthetic Escape keypress, which the
      // Dialog root listens for. Cleaner than reaching into Radix internals.
      const closeEvt = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true });
      document.dispatchEvent(closeEvt);
    }
    setDragY(0);
    dragStartRef.current = null;
  }

  let layoutClasses = CENTERED_CONTENT_CLASSES;
  if (useSheet) layoutClasses = SHEET_CONTENT_CLASSES;
  else if (useFullscreen) layoutClasses = FULLSCREEN_CONTENT_CLASSES;

  const style = useSheet && dragY > 0 ? { transform: `translateY(${dragY}px)` } : undefined;

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(layoutClasses, className)}
        style={style}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        {...props}
      >
        {useSheet && <SheetDragHandle />}
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
